import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  buildTemplateInsert,
  deletionMessage,
  isReadableDocxPackage,
  nextTemplateStatus,
  nextTemplateVersion,
  sortTemplates,
  templateLabel,
  validateTemplateFile
} from '../../lib/templateManagement';

const TEMPLATE_BUCKET = 'document-templates';
const emptyForm = { name: '', description: '', template_type: 'estate_plan', active: true };
const formatError = (error, fallback) => [error?.message, error?.details, error?.hint].filter(Boolean).join(' ') || fallback;
const safeFileName = (name) => name.replace(/[^a-zA-Z0-9._-]/g, '_');

const TemplateManagement = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [generatedDocuments, setGeneratedDocuments] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [versionSource, setVersionSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [statusChangingId, setStatusChangingId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const loadTemplates = async () => {
    if (!profile?.law_firm_id) return;
    try {
      setLoading(true);
      const [templatesRes, generatedRes] = await Promise.all([
        supabase
          .from('document_templates')
          .select('*')
          .eq('law_firm_id', profile.law_firm_id),
        supabase
          .from('generated_documents')
          .select('id, document_template_id')
      ]);
      if (templatesRes.error) throw templatesRes.error;
      if (generatedRes.error) throw generatedRes.error;
      setTemplates(sortTemplates(templatesRes.data || []));
      setGeneratedDocuments(generatedRes.data || []);
    } catch (loadError) {
      setError(formatError(loadError, 'Unable to load templates.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTemplates(); }, [profile?.law_firm_id]);

  const validateReadableDocx = async (file) => {
    const fileError = validateTemplateFile(file);
    if (fileError) throw new Error(fileError);
    try {
      const { default: JSZip } = await import('jszip');
      const zip = await JSZip.loadAsync(await file.arrayBuffer());
      if (!isReadableDocxPackage(zip.files)) {
        throw new Error('The selected file is not a readable DOCX package.');
      }
    } catch (validationError) {
      if (validationError.message?.includes('readable DOCX')) throw validationError;
      throw new Error('The selected file could not be read as a DOCX package.');
    }
  };

  const startNewVersion = (template) => {
    setVersionSource(template);
    setForm({
      name: template.name || '',
      description: template.description || '',
      template_type: template.template_type || 'estate_plan',
      active: true
    });
    setSelectedFile(null);
    setNotice(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetUpload = () => {
    setForm(emptyForm);
    setSelectedFile(null);
    setVersionSource(null);
  };

  const uploadTemplate = async (event) => {
    event.preventDefault();
    if (uploading) return;
    if (!form.name.trim()) {
      setError('Provide a display name for the template.');
      return;
    }
    let storagePath = null;
    try {
      setUploading(true);
      setError(null);
      setNotice(null);
      await validateReadableDocx(selectedFile);
      const version = versionSource
        ? nextTemplateVersion(templates, versionSource)
        : nextTemplateVersion(templates, form);
      storagePath = `${profile.law_firm_id}/${Date.now()}-${crypto.randomUUID()}-${safeFileName(selectedFile.name)}`;
      const { error: storageError } = await supabase.storage.from(TEMPLATE_BUCKET).upload(storagePath, selectedFile, {
        cacheControl: '3600',
        contentType: selectedFile.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        upsert: false
      });
      if (storageError) throw storageError;
      const { data: insertedTemplate, error: insertError } = await supabase
        .from('document_templates')
        .insert(buildTemplateInsert({
          file: selectedFile,
          form,
          lawFirmId: profile.law_firm_id,
          userId: profile.id,
          storagePath,
          version
        }))
        .select()
        .single();
      if (insertError) throw insertError;
      setNotice(versionSource
        ? `${templateLabel(insertedTemplate)} was created as a new template version.`
        : `${templateLabel(insertedTemplate)} was uploaded.`);
      resetUpload();
      await loadTemplates();
    } catch (uploadError) {
      if (storagePath) await supabase.storage.from(TEMPLATE_BUCKET).remove([storagePath]);
      setError(formatError(uploadError, 'Unable to upload the template.'));
    } finally {
      setUploading(false);
    }
  };

  const changeStatus = async (template) => {
    try {
      setStatusChangingId(template.id);
      setError(null);
      const nextStatus = nextTemplateStatus(template);
      const { error: updateError } = await supabase
        .from('document_templates')
        .update({ status: nextStatus })
        .eq('id', template.id)
        .eq('law_firm_id', profile.law_firm_id);
      if (updateError) throw updateError;
      setNotice(`${templateLabel(template)} is now ${nextStatus === 'active' ? 'Active' : 'Inactive'}.`);
      await loadTemplates();
    } catch (statusError) {
      setError(formatError(statusError, 'Unable to change the template status.'));
    } finally {
      setStatusChangingId(null);
    }
  };

  if (!profile?.law_firm_id) return <div className="p-8 text-center bg-white rounded-lg border border-slate-200">Law firm access is required.</div>;

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center"><button onClick={() => navigate(-1)} className="mr-4 p-2 rounded-full hover:bg-white text-slate-400"><SafeIcon icon={FiIcons.FiArrowLeft} className="h-6 w-6" /></button><div><h1 className="text-2xl font-bold text-slate-900">Template Management</h1><p className="text-sm text-slate-500 mt-1">Upload, version, activate, and retain private DOCX templates.</p></div></div>
        <button onClick={loadTemplates} className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"><SafeIcon icon={FiIcons.FiRefreshCw} className="mr-2 h-4 w-4" />Refresh</button>
      </div>

      {notice && <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-md text-sm text-green-700">{notice}</div>}
      {error && <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md text-sm text-red-700">{error}</div>}

      <form onSubmit={uploadTemplate} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between"><div><h2 className="font-bold text-slate-800">{versionSource ? `Upload new version of ${templateLabel(versionSource)}` : 'Upload template'}</h2><p className="text-xs text-slate-500 mt-1">DOCX only, up to 20 MB. Technical file checks only.</p></div>{versionSource && <button type="button" onClick={resetUpload} className="text-xs font-bold uppercase tracking-widest text-slate-600">Cancel version</button>}</div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4"><label className="text-sm font-medium text-slate-700">Display name<input required disabled={Boolean(versionSource)} value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 disabled:bg-slate-100" placeholder="Revocable Living Trust" /></label><label className="text-sm font-medium text-slate-700">Document type<select disabled={Boolean(versionSource)} value={form.template_type} onChange={(event) => setForm((current) => ({ ...current, template_type: event.target.value }))} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 bg-white disabled:bg-slate-100"><option value="estate_plan">Estate plan</option><option value="will">Will</option><option value="trust">Trust</option><option value="power_of_attorney">Power of attorney</option><option value="healthcare_directive">Healthcare directive</option><option value="letter">Letter</option><option value="other">Other</option></select></label><label className="text-sm font-medium text-slate-700 md:col-span-2">Description <span className="font-normal text-slate-400">(optional)</span><textarea rows={2} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2" /></label><label className="text-sm font-medium text-slate-700 md:col-span-2">DOCX file<input type="file" required accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(event) => setSelectedFile(event.target.files?.[0] || null)} className="mt-1 block w-full text-sm text-slate-600" /></label><label className="flex items-center text-sm text-slate-600"><input type="checkbox" checked={form.active} onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))} className="mr-2" />Make this version Active</label></div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100"><button type="submit" disabled={uploading || !selectedFile} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md disabled:opacity-50">{uploading ? <><SafeIcon icon={FiIcons.FiLoader} className="animate-spin mr-2 h-4 w-4" />Uploading...</> : <><SafeIcon icon={FiIcons.FiUpload} className="mr-2 h-4 w-4" />{versionSource ? 'Upload New Version' : 'Upload Template'}</>}</button></div>
      </form>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50"><h2 className="font-bold text-slate-800">Law firm templates</h2><p className="text-xs text-slate-500 mt-1">Inactive templates stay available for draft history but are not offered for new generation.</p></div>
        {loading ? <div className="p-10 text-center text-slate-500"><SafeIcon icon={FiIcons.FiLoader} className="h-6 w-6 animate-spin mx-auto mb-2" />Loading templates...</div> : templates.length === 0 ? <div className="p-10 text-center text-slate-500">No templates have been uploaded for this law firm.</div> : <div className="divide-y divide-slate-100">{templates.map((template) => <article key={template.id} className="p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold text-slate-900">{templateLabel(template)}</h3><span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full border ${template.status === 'active' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>{template.status === 'active' ? 'Active' : 'Inactive'}</span><span className="text-xs text-slate-500 capitalize">{template.template_type?.replaceAll('_', ' ')}</span></div><p className="mt-2 text-sm text-slate-600">{template.description || 'No description'}</p><p className="mt-2 text-xs text-slate-500">{template.file_name || 'Original filename unavailable'} · Uploaded {format(new Date(template.created_at), 'MMM d, yyyy h:mm a')}</p></div><div className="flex flex-wrap gap-2"><button onClick={() => startNewVersion(template)} className="px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">Upload New Version</button><button onClick={() => changeStatus(template)} disabled={statusChangingId === template.id} className="px-3 py-2 text-xs font-bold uppercase tracking-widest rounded-md bg-slate-800 text-white disabled:opacity-50">{statusChangingId === template.id ? 'Saving...' : template.status === 'active' ? 'Make Inactive' : 'Make Active'}</button><span title={deletionMessage(template, generatedDocuments)} className="px-3 py-2 text-xs text-slate-400">Retained</span></div></article>)}</div>}
      </section>
    </div>
  );
};

export default TemplateManagement;
